import React, { useState, useEffect } from 'react';
import { 
    MapPin, Waypoints, Plus, Edit, Trash2, Search, ArrowRight, X, DollarSign, Save, AlertCircle, FileSpreadsheet 
} from 'lucide-react';
import api from '../../api/axios';

const RutasPage = () => {
    const [activeTab, setActiveTab] = useState('sucursales');
    const [branches, setBranches] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // --- ESTADOS PARA MODALES ---
    const [showBranchModal, setShowBranchModal] = useState(false);
    const [showRouteModal, setShowRouteModal] = useState(false); 
    const [showRatesModal, setShowRatesModal] = useState(false);
    
    // --- ESTADOS DE EDICIÓN ---
    const [editingItem, setEditingItem] = useState(null); 
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routeRates, setRouteRates] = useState([]);
    const [loadingRates, setLoadingRates] = useState(false);

    // --- FORMULARIO DE TARIFA ---
    const [rateForm, setRateForm] = useState({ service_type: 'estandar', weight_kg: '', price: '' });
    const [isEditingRate, setIsEditingRate] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [branchesRes, routesRes] = await Promise.all([
                api.get('/branches'),
                api.get('/routes')
            ]);
            const rawBranches = branchesRes.data?.data || branchesRes.data;
            const rawRoutes = routesRes.data?.data || routesRes.data;
            setBranches(Array.isArray(rawBranches) ? rawBranches : []);
            setRoutes(Array.isArray(rawRoutes) ? rawRoutes : []);
        } catch (error) {
            console.error("Error al cargar datos:", error);
            setBranches([]);
            setRoutes([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    // ==========================================
    // GESTIÓN DE SUCURSALES (Sincronizado con tu BD)
    // ==========================================
    const handleSaveBranch = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);
    
    // --- 🛡️ CAPTURA DE DATOS ESPECIALES ---
    // FormData no incluye checkboxes si no están marcados, por eso usamos formData.has()
    data.is_main = formData.has('is_main') ? 1 : 0;

    // --- 🛡️ INICIO DE VALIDACIONES FRONTEND ---
    
    // 1. Limpiar espacios en blanco al inicio o final (Trim)
    data.name = data.name.trim();
    data.city = data.city.trim();
    
    // 2. Validar Teléfono (Solo números, exactamente 10)
    // Usamos data.phone directamente porque ya viene limpio por el onInput del input
    if (data.phone) {
        const phoneRegex = /^\d{10}$/; 
        if (!phoneRegex.test(data.phone)) {
            return alert("⚠️ El teléfono debe contener exactamente 10 números.");
        }
    }

    // 3. Validar Nombre y Ciudad (Mínimo 3 caracteres)
    if (data.name.length < 3 || data.city.length < 3) {
        return alert("⚠️ El nombre de la sucursal y la ciudad deben tener al menos 3 caracteres.");
    }

    // --- FIN DE VALIDACIONES ---

    try {
        if (editingItem) {
            await api.put(`/branches/${editingItem.id}`, data);
        } else {
            await api.post('/branches', data);
        }
        
        // Limpieza y cierre
        setShowBranchModal(false);
        setEditingItem(null);
        fetchData(); // Recarga la lista para ver los cambios
        alert("✅ Sucursal guardada correctamente.");
    } catch (error) { 
        const errorMsg = error.response?.data?.message || "Error al procesar la sucursal en el servidor.";
        console.error("Error completo:", error);
        alert(`❌ Error del Servidor:\n${errorMsg}`);
    }
};

    const handleDeleteBranch = async (id) => {
        if (!window.confirm("¿Estás seguro de eliminar esta sucursal?")) return;
        try {
            await api.delete(`/branches/${id}`);
            fetchData();
        } catch (error) { alert("Error al eliminar. Verifica que no tenga rutas asociadas."); }
    };

    // ==========================================
    // GESTIÓN DE RUTAS
    // ==========================================
    const handleSaveRoute = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = Object.fromEntries(formData);

        if (data.origin_branch_id === data.destination_branch_id) {
            return alert("❌ El origen y el destino no pueden ser iguales.");
        }

        try {
            await api.post('/routes', data);
            setShowRouteModal(false);
            fetchData();
        } catch (error) { alert("Error al crear la conexión operativa."); }
    };

    const handleDeleteRoute = async (id) => {
        if (!window.confirm("¿Eliminar esta ruta operativa?")) return;
        try {
            await api.delete(`/routes/${id}`);
            fetchData();
        } catch (error) { alert("Error al eliminar la ruta."); }
    };

    // ==========================================
    // GESTIÓN DE TARIFAS
    // ==========================================
    const loadRates = async (routeId) => {
        setLoadingRates(true);
        try {
            const response = await api.get(`/tarifas/${routeId}`);
            setRouteRates(response.data);
        } catch (error) { console.error(error); }
        finally { setLoadingRates(false); }
    };

    const handleOpenRates = (route) => {
        setSelectedRoute(route);
        setShowRatesModal(true);
        loadRates(route.id);
    };

    const handleSaveRate = async () => {
    // Validamos campos básicos
    if (!rateForm.weight_kg || !rateForm.price) {
        return alert("⚠️ Por favor, ingresa el peso y el precio.");
    }
    
    try {
        const data = { 
            route_id: selectedRoute.id,
            service_type: rateForm.service_type,
            weight_kg: parseFloat(rateForm.weight_kg),
            price: parseFloat(rateForm.price),
            // Agregamos este campo que pide tu modelo RouteRate
            ferenc_fee: 0 
        };

        if (isEditingRate) {
            await api.put(`/rates/${isEditingRate}`, data);
        } else {
            await api.post('/rates', data);
        }

        // Limpiar y recargar
        setRateForm({ service_type: 'estandar', weight_kg: '', price: '' });
        setIsEditingRate(null);
        loadRates(selectedRoute.id);
        
    } catch (error) { 
        // Extraemos el error REAL de Laravel
        const errorServidor = error.response?.data?.message || 
                              error.response?.data?.error || 
                              "Error desconocido en el servidor";
                              
        console.error("Detalle técnico del error:", error.response?.data);
        
        // Si el error es de SQL, nos dirá qué columna falta
        alert(`❌ Error al guardar:\n${errorServidor}`); 
    }
};

    const deleteRate = async (id) => {
        if (!window.confirm("¿Eliminar esta tarifa?")) return;
        try {
            await api.delete(`/rates/${id}`);
            loadRates(selectedRoute.id);
        } catch (error) { alert("Error al eliminar."); }
    };

    // --- FILTRADO ---
    const filteredBranches = branches.filter(b => b.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    const filteredRoutes = routes.filter(r => 
        r.origin_branch?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.destination_branch?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const handleExportRoutes = async () => {
        try {
            const response = await api.get('/export/routes', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Reporte_Tarifas.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Error al exportar:", error);
            alert("❌ No se pudo exportar el reporte.");
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-12">
            {/* HEADER */}
<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
    <div>
        <h3 className="text-3xl font-black uppercase tracking-tighter text-[#CE010B] dark:text-white">
            Logística Ocurre
        </h3>
        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest italic">
            Administración de Sedes y Tarifas
        </p>
    </div>

    <div className="flex items-center gap-3 w-full md:w-auto">
        {/* BUSCADOR */}
        <div className="relative flex-1 md:w-64 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#CE010B] transition-colors" size={18} />
            <input 
                type="text" 
                placeholder="Buscar..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-6 py-3 rounded-2xl bg-white dark:bg-black/20 border-none ring-1 ring-black/5 outline-none focus:ring-2 focus:ring-[#CE010B] font-bold text-sm transition-all" 
            />
        </div>

        {/* BOTÓN EXCEL (Solo visible en rutas) */}
        {activeTab === 'rutas' && (
            <button 
                onClick={handleExportRoutes}
                className="bg-emerald-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-xs shadow-lg hover:shadow-emerald-600/30 hover:-translate-y-0.5 transition-all active:scale-95 flex items-center gap-2"
            >
                <FileSpreadsheet size={18} /> Excel
            </button>
        )}

        {/* BOTÓN NUEVO */}
        <button 
            onClick={() => { 
                setEditingItem(null); 
                activeTab === 'sucursales' ? setShowBranchModal(true) : setShowRouteModal(true); 
            }}
            className="bg-[#CE010B] text-white px-6 py-3 rounded-2xl font-black uppercase text-xs shadow-lg hover:shadow-[#CE010B]/30 hover:-translate-y-0.5 transition-all active:scale-95 flex items-center gap-2"
        >
            <Plus size={18} /> Nuevo
        </button>
    </div>
</div>

            {/* TABS */}
            <div className="flex gap-2 p-1.5 bg-white dark:bg-black/20 rounded-2xl border border-black/5 w-fit shadow-sm">
                <button onClick={() => setActiveTab('sucursales')} className={`px-8 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'sucursales' ? 'bg-[#CE010B] text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}>
                    <MapPin size={14} className="inline mr-2"/> Sucursales
                </button>
                <button onClick={() => setActiveTab('rutas')} className={`px-8 py-3 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all ${activeTab === 'rutas' ? 'bg-[#CE010B] text-white shadow-md' : 'text-gray-400 hover:text-gray-600'}`}>
                    <Waypoints size={14} className="inline mr-2"/> Rutas Operativas
                </button>
            </div>

            {/* TABLAS */}
            <div className="bg-white dark:bg-black/40 rounded-[2.5rem] border border-black/5 dark:border-white/10 overflow-hidden shadow-xl">
                {activeTab === 'sucursales' ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-white/5 border-b border-black/5 text-[10px] uppercase font-black text-gray-400 tracking-widest">
                                    <th className="py-6 px-8">ID</th>
                                    <th className="py-6 px-4">Sucursal</th>
                                    <th className="py-6 px-4">Ciudad</th>
                                    <th className="py-6 px-4">Contacto</th>
                                    <th className="py-6 px-8 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                {filteredBranches.map(b => (
                                    <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors group">
                                        <td className="py-5 px-8 font-mono text-xs text-gray-400">#{b.id}</td>
                                        <td className="py-5 px-4 font-black uppercase text-sm text-[#CE010B] dark:text-white">{b.name}</td>
                                        <td className="py-5 px-4 text-xs font-bold text-gray-600 dark:text-gray-400">{b.city}</td>
                                        <td className="py-5 px-4 text-xs font-bold text-gray-500">{b.phone || 'N/A'}</td>
                                        <td className="py-5 px-8 text-right space-x-2">
                                            <button onClick={() => { setEditingItem(b); setShowBranchModal(true); }} className="p-2.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all"><Edit size={16}/></button>
                                            <button onClick={() => handleDeleteBranch(b.id)} className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 size={16}/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-white/5 border-b border-black/5 text-[10px] uppercase font-black text-gray-400 tracking-widest">
                                    <th className="py-6 px-8">Trayecto Operativo (Origen → Destino)</th>
                                    <th className="py-6 px-8 text-right">Gestión de Precios</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-black/5 dark:divide-white/5">
                                {filteredRoutes.map(r => (
                                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                        <td className="py-5 px-8">
                                            <div className="flex items-center gap-4">
                                                <span className="font-black uppercase text-sm dark:text-white">{r.origin_branch?.name}</span>
                                                <ArrowRight size={20} className="text-[#CE010B] opacity-50"/>
                                                <span className="font-black uppercase text-sm dark:text-white">{r.destination_branch?.name}</span>
                                            </div>
                                        </td>
                                        <td className="py-5 px-8 text-right space-x-3">
                                            <button onClick={() => handleOpenRates(r)} className="bg-green-600 text-white px-5 py-2.5 rounded-xl text-[10px] font-black uppercase hover:bg-green-700 shadow-md transition-all inline-flex items-center gap-2">
                                                <DollarSign size={14}/> Configurar Tarifas
                                            </button>
                                            <button onClick={() => handleDeleteRoute(r.id)} className="p-2.5 text-gray-400 hover:text-red-500 transition-all"><Trash2 size={18}/></button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* MODAL: SUCURSAL */}
{showBranchModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
        <form onSubmit={handleSaveBranch} className="bg-white dark:bg-[#1A1A1A] w-full max-w-lg rounded-[2.5rem] shadow-2xl p-8 border border-white/10 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black uppercase text-[#CE010B] dark:text-white">{editingItem ? 'Editar Sede' : 'Nueva Sede'}</h3>
                <button type="button" onClick={() => setShowBranchModal(false)}><X size={24}/></button>
            </div>
            
            <div className="space-y-4">
                {/* CAMPOS BÁSICOS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input name="name" defaultValue={editingItem?.name} required type="text" placeholder="Nombre Sucursal" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                    <input name="city" defaultValue={editingItem?.city} required type="text" placeholder="Ciudad" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                </div>

                <input name="direccion" defaultValue={editingItem?.direccion} type="text" placeholder="Dirección completa (opcional)" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                
                <div className="grid grid-cols-2 gap-4">
                    <input name="phone" defaultValue={editingItem?.phone} type="tel" maxLength="10" 
                        onInput={(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')}
                        placeholder="Teléfono (opcional)" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                    <input name="email_contacto" defaultValue={editingItem?.email_contacto} type="email" placeholder="Email contacto (opcional)" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                </div>

                {/* COORDENADAS Y MAPS URL */}
                <div className="grid grid-cols-2 gap-4">
                    <input name="lat" defaultValue={editingItem?.lat} type="number" step="any" placeholder="Latitud (opcional)" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                    <input name="lng" defaultValue={editingItem?.lng} type="number" step="any" placeholder="Longitud (opcional)" className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" />
                </div>

                <input 
                    name="google_maps_url" 
                    defaultValue={editingItem?.google_maps_url} 
                    type="url" 
                    placeholder="Enlace oficial de Google Maps (ej: https://maps.app.goo.gl/...)" 
                    className="w-full p-4 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm" 
                />

                {/* SUCURSAL PRINCIPAL */}
                <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-2xl">
                    <input type="checkbox" name="is_main" id="is_main" defaultChecked={editingItem?.is_main} className="w-5 h-5 accent-[#CE010B] cursor-pointer" />
                    <label htmlFor="is_main" className="text-[11px] font-black uppercase text-gray-400 cursor-pointer">Marcar como Matriz</label>
                </div>

                <button type="submit" className="w-full bg-[#CE010B] text-white p-5 rounded-2xl font-black uppercase tracking-widest mt-2 hover:bg-black transition-all">
                    {editingItem ? 'Actualizar' : 'Guardar'}
                </button>
            </div>
        </form>
    </div>
)}

            {/* MODAL: NUEVA RUTA */}
            {showRouteModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in zoom-in-95">
                    <form onSubmit={handleSaveRoute} className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 border border-white/10">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-black uppercase text-[#CE010B] dark:text-white">Nueva Conexión</h3>
                            <button type="button" onClick={() => setShowRouteModal(false)}><X size={24}/></button>
                        </div>
                        <div className="space-y-5">
                            <select name="origin_branch_id" required className="w-full p-5 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm outline-none">
                                <option value="">Origen...</option>
                                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                            <select name="destination_branch_id" required className="w-full p-5 rounded-2xl bg-gray-50 dark:bg-white/5 border-none font-bold text-sm outline-none">
                                <option value="">Destino...</option>
                                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                            <button type="submit" className="w-full bg-[#CE010B] text-white p-5 rounded-2xl font-black uppercase tracking-widest mt-4 shadow-xl transition-all">
                                Crear Trayectoria
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* MODAL: TARIFAS */}
            {showRatesModal && selectedRoute && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-2xl max-h-[85vh] rounded-[3rem] shadow-2xl flex flex-col overflow-hidden border border-white/10">
                        <div className="p-8 border-b border-black/5 flex justify-between items-center bg-gray-50/50 dark:bg-white/5">
                            <div>
                                <h3 className="text-2xl font-black uppercase text-[#CE010B] dark:text-white">Tabulador TBT</h3>
                                <p className="text-[11px] font-black text-gray-500 uppercase tracking-widest">{selectedRoute.origin_branch?.name} → {selectedRoute.destination_branch?.name}</p>
                            </div>
                            <button onClick={() => setShowRatesModal(false)} className="p-2.5 bg-white dark:bg-white/10 rounded-full shadow-sm hover:scale-110 transition-all"><X size={20}/></button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar space-y-8">
                            <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-[2rem] border-2 border-dashed border-gray-200 dark:border-white/10 mb-8">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <select value={rateForm.service_type} onChange={e => setRateForm({...rateForm, service_type: e.target.value})} className="p-4 rounded-xl text-xs font-bold bg-white dark:bg-black/20 border-none ring-1 ring-black/5 outline-none">
                                        <option value="estandar">Estándar</option>
                                        <option value="expres">Exprés</option>
                                    </select>
                                    <input type="number" placeholder="KG" value={rateForm.weight_kg} onChange={e => setRateForm({...rateForm, weight_kg: e.target.value})} className="p-4 rounded-xl text-xs font-bold bg-white dark:bg-black/20 border-none ring-1 ring-black/5 outline-none" />
                                    <input type="number" placeholder="$ Precio" value={rateForm.price} onChange={e => setRateForm({...rateForm, price: e.target.value})} className="p-4 rounded-xl text-xs font-bold bg-white dark:bg-black/20 border-none ring-1 ring-black/5 outline-none" />
                                    <button onClick={handleSaveRate} className="bg-black dark:bg-[#CE010B] text-white rounded-xl font-black uppercase text-[10px] hover:scale-105 transition-all flex items-center justify-center gap-2">
                                        <Save size={16}/> {isEditingRate ? 'OK' : 'Add'}
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-3">
                                {routeRates.map(rate => (
                                    <div key={rate.id} className="flex items-center justify-between p-4 bg-white dark:bg-white/5 rounded-2xl border border-black/5 group hover:border-[#CE010B]/30 transition-all shadow-sm">
                                        <div className="flex items-center gap-6">
                                            <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase ${rate.service_type === 'expres' ? 'bg-[#CE010B]/10 text-[#CE010B]' : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300'}`}>
                                                {rate.service_type}
                                            </span>
                                            <span className="text-sm font-bold dark:text-white">Hasta {rate.weight_kg}kg</span>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <span className="text-lg font-black text-green-600">${rate.price}</span>
                                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => { setIsEditingRate(rate.id); setRateForm({service_type: rate.service_type, weight_kg: rate.weight_kg, price: rate.price}) }} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-all"><Edit size={14}/></button>
                                                <button onClick={() => deleteRate(rate.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14}/></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RutasPage;